import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Image, Pressable, RefreshControl, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink, MessageSquareMore } from 'lucide-react-native';
import AppHeader from '@/components/AppHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiRequest } from '@/lib/queryClient';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { colors } from '@/lib/theme';

const feedbackOverlayColor = 'rgba(22, 36, 28, 0.82)';

type AdminFeedbackEntry = {
  id: string;
  userId: string;
  category: string;
  message: string;
  page: string | null;
  screenshotObjectPath: string | null;
  userAgent: string | null;
  viewport?: unknown;
  status: string | null;
  createdAt: string | null;
  user: {
    id: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
  };
};

function formatUserName(entry: AdminFeedbackEntry) {
  const name = [entry.user.firstName, entry.user.lastName].filter(Boolean).join(' ').trim();
  return name || entry.user.email || 'Unknown user';
}

function formatCreatedAt(value: string | null) {
  if (!value) {
    return 'Unknown time';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}

function ScreenshotPreview({ feedbackId }: { feedbackId: string }) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;
    let objectUrl: string | null = null;

    const load = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const response = await apiRequest(`/api/admin/feedback/${feedbackId}/screenshot`, 'GET');
        if (!response.ok) {
          throw new Error(`Failed to load screenshot (${response.status})`);
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!isCancelled) {
          setImageUrl(objectUrl);
        }
      } catch (error: any) {
        if (!isCancelled) {
          setLoadError(error?.message || 'Could not load screenshot.');
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();
    return () => {
      isCancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [feedbackId]);

  if (isLoading) {
    return (
      <View style={styles.screenshotState}>
        <ActivityIndicator color={colors.ink.press} />
        <Text style={styles.stateText}>Loading screenshot…</Text>
      </View>
    );
  }

  if (loadError || !imageUrl) {
    return (
      <View style={styles.screenshotState}>
        <Text style={styles.stateText}>{loadError || 'Screenshot unavailable.'}</Text>
      </View>
    );
  }

  return (
    <Pressable
      onPress={() => {
        if (typeof window !== 'undefined') {
          window.open(imageUrl, '_blank', 'noopener,noreferrer');
        }
      }}
      style={styles.screenshotPressable}
    >
      <Image source={{ uri: imageUrl }} style={styles.screenshotImage} resizeMode="cover" />
      <View style={styles.screenshotOverlay}>
        <ExternalLink size={14} color={colors.text.inverse} />
        <Text style={styles.screenshotOverlayText}>Open full image</Text>
      </View>
    </Pressable>
  );
}

export default function FeedbackScreen() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const isAdmin = Boolean(user?.isAdmin);

  const feedbackQuery = useQuery(
    trpc.admin.feedback.queryOptions(undefined, {
      enabled: isAdmin,
    }),
  );

  const feedbackEntries = useMemo(() => feedbackQuery.data ?? [], [feedbackQuery.data]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await queryClient.invalidateQueries(trpc.admin.feedback.queryFilter());
    } catch (error: any) {
      toast.show({
        type: 'error',
        title: 'Refresh Failed',
        description: error?.message || 'Could not refresh feedback.',
      });
    } finally {
      setRefreshing(false);
    }
  };

  if (!isAdmin) {
    return (
      <View style={styles.screen}>
        <AppHeader title="Feedback" />
        <View style={styles.centerScreen}>
          <AlertTriangle size={32} color={colors.warning[600]} />
          <Text style={styles.emptyTitle}>Admin access required</Text>
          <Text style={styles.emptyDescription}>
            Only administrators can access the Feedback page.
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <AppHeader title="Feedback" />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => void onRefresh()} />}
      >
        <View style={styles.hero}>
          <View style={styles.heroText}>
            <Text style={styles.pageSubtitle}>Review incoming notes and screenshots from the web app.</Text>
          </View>
          <Button variant="outline" onPress={() => void onRefresh()} disabled={feedbackQuery.isFetching}>
            Refresh
          </Button>
        </View>

        {feedbackQuery.isLoading ? (
          <View style={styles.centerScreen}>
            <ActivityIndicator color={colors.ink.press} />
            <Text style={styles.stateText}>Loading feedback…</Text>
          </View>
        ) : feedbackQuery.error ? (
          <View style={styles.centerScreen}>
            <Text style={styles.emptyTitle}>Could not load feedback</Text>
            <Text style={styles.emptyDescription}>{feedbackQuery.error.message}</Text>
          </View>
        ) : feedbackEntries.length === 0 ? (
          <View style={styles.centerScreen}>
            <MessageSquareMore size={32} color={colors.text.secondary} />
            <Text style={styles.emptyTitle}>No feedback yet</Text>
            <Text style={styles.emptyDescription}>New web submissions will show up here.</Text>
          </View>
        ) : (
          feedbackEntries.map((entry) => (
            <Card key={entry.id} style={styles.card}>
              <CardHeader>
                <View style={styles.cardHeaderRow}>
                  <View style={styles.cardHeaderText}>
                    <CardTitle>{formatUserName(entry)}</CardTitle>
                    <Text style={styles.metaText}>
                      {entry.user.email || 'No email'} · {formatCreatedAt(entry.createdAt)}
                    </Text>
                  </View>
                  <View style={styles.statusBadge}>
                    <Text style={styles.statusText}>{entry.status || 'open'}</Text>
                  </View>
                </View>
              </CardHeader>
              <CardContent>
                <View style={styles.metaBlock}>
                  <Text style={styles.metaLabel}>Page</Text>
                  <Text style={styles.metaValue}>{entry.page || 'Unknown page'}</Text>
                </View>

                <View style={styles.messageBlock}>
                  <Text style={styles.metaLabel}>Message</Text>
                  <Text style={styles.messageText}>{entry.message}</Text>
                </View>

                {entry.screenshotObjectPath ? (
                  <View style={styles.screenshotBlock}>
                    <Text style={styles.metaLabel}>Screenshot</Text>
                    <ScreenshotPreview feedbackId={entry.id} />
                  </View>
                ) : null}
              </CardContent>
            </Card>
          ))
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create(() => ({
  card: {
    marginBottom: 16,
  },
  cardHeaderRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'space-between',
  },
  cardHeaderText: {
    flex: 1,
  },
  centerScreen: {
    alignItems: 'center',
    flex: 1,
    gap: 10,
    justifyContent: 'center',
    minHeight: 280,
    paddingHorizontal: 24,
  },
  content: {
    padding: 20,
    paddingBottom: 48,
  },
  emptyDescription: {
    color: colors.ink.pressMuted,
    fontSize: 14,
    lineHeight: 20,
    maxWidth: 420,
    textAlign: 'center',
  },
  emptyTitle: {
    color: colors.ink.press,
    fontFamily: 'Archivo_900Black',
    fontSize: 20,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  hero: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  heroText: {
    flex: 1,
    minWidth: 0,
  },
  messageBlock: {
    marginTop: 12,
  },
  messageText: {
    color: colors.ink.press,
    fontSize: 15,
    lineHeight: 22,
    marginTop: 4,
  },
  metaBlock: {
    gap: 4,
  },
  metaLabel: {
    color: colors.ink.pressMuted,
    fontFamily: 'IBMPlexMono_600SemiBold',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  metaText: {
    color: colors.ink.pressMuted,
    fontSize: 13,
    marginTop: 4,
  },
  metaValue: {
    color: colors.ink.press,
    fontSize: 14,
  },
  pageSubtitle: {
    color: colors.ink.pressMuted,
    flexShrink: 1,
    fontSize: 14,
    marginTop: 4,
  },
  screen: {
    backgroundColor: colors.ink.paper,
    flex: 1,
  },
  screenshotBlock: {
    marginTop: 16,
  },
  screenshotImage: {
    height: 220,
    width: '100%',
  },
  screenshotOverlay: {
    alignItems: 'center',
    backgroundColor: feedbackOverlayColor,
    bottom: 0,
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
    left: 0,
    paddingHorizontal: 12,
    paddingVertical: 10,
    position: 'absolute',
    right: 0,
  },
  screenshotOverlayText: {
    color: colors.ink.paper,
    fontSize: 13,
    fontWeight: '700',
  },
  screenshotPressable: {
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 3,
    marginTop: 8,
    overflow: 'hidden',
  },
  screenshotState: {
    alignItems: 'center',
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 3,
    gap: 10,
    justifyContent: 'center',
    marginTop: 8,
    minHeight: 160,
    padding: 16,
  },
  scroll: {
    flex: 1,
  },
  stateText: {
    color: colors.ink.pressMuted,
    fontSize: 14,
  },
  statusBadge: {
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  statusText: {
    color: colors.ink.press,
    fontFamily: 'IBMPlexMono_600SemiBold',
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'capitalize',
  },
}));
